// Import React hooks for state management and side effects
import { useEffect, useState } from 'react';
// Import form components for building the UI
import { FormSection, FormSelect } from '@/components';
// Import translation hook for internationalization
import { useTranslation } from 'react-i18next';
// Import device model hooks for fetching device data
import {
  useDeviceModelDetail,
  useDeviceModels,
} from '@/features/device-models/hooks';
import { FormSectionInfo } from '@/components/FormSection';

// Define the shape of device assignment state for type safety
export interface DocumentDeviceAssignmentState {
  deviceModelId: number;
  userLevel: number;
}

// Define component props with clear type definitions
interface DocumentDeviceAssignmentSectionProps {
  state: DocumentDeviceAssignmentState | undefined;
  isLocked: boolean;
  showErrors: boolean;
  onStateChange: (
    state: DocumentDeviceAssignmentState | undefined,
    hasError: boolean
  ) => void;
}

/**
 * @component DocumentDeviceAssignmentSection
 * @description Renders device model and user level assignment controls for a document file form.
 *
 * @props
 *   state (DocumentDeviceAssignmentState, required) - Current state with deviceModelId and userLevel.
 *   isLocked (boolean, required) - Disables editing while metadata is read-only.
 *   showErrors (boolean, required) - Whether to show validation errors for missing required values.
 *   onStateChange ((state, hasError) => void, required) - Callback when state changes, includes error flag.
 *
 */
export function DocumentDeviceAssignmentSection({
  state,
  isLocked,
  showErrors,
  onStateChange,
}: DocumentDeviceAssignmentSectionProps) {
  // Initialize translation hook for localized text
  const { t } = useTranslation();

  // Internal state management for form fields
  const [deviceModelId, setDeviceModelId] = useState<number | undefined>(undefined);
  const [userLevel, setUserLevel] = useState<number | undefined>(undefined);

  // Keep internal state in sync with external prop changes
  useEffect(() => {
    if (state) {
      setDeviceModelId(state.deviceModelId);
      setUserLevel(state.userLevel);
    } else {
      setDeviceModelId(undefined);
      setUserLevel(undefined);
    }
  }, [state]);

  // Fetch device models and selected device model details from API
  const { data: deviceModels = [], isError: hasDeviceModelError } = useDeviceModels();
  const { data: selectedDeviceModelDetail, isError: hasUserLevelError } = useDeviceModelDetail(deviceModelId ?? 0);

  // Derive computed values for conditional rendering and validation
  const shouldShowUserLevel = deviceModelId !== undefined;
  const userLevels = selectedDeviceModelDetail?.userLevels ?? [];

  // Validate and reset user level when device model changes or becomes invalid
  useEffect(() => {
    // If no device model is selected or no user levels available, clear user level
    if (!shouldShowUserLevel || userLevels.length === 0) {
      if (userLevel !== undefined) {
        setUserLevel(undefined);
        onStateChange(undefined, false);
      }
      return;
    }

    // Check if the currently selected user level is still valid for the new device model
    const hasSelectedUserLevel = userLevels.some(
      (level) => level.id === userLevel
    );

    // If selected user level is no longer valid, clear it and mark as error
    if (!hasSelectedUserLevel && userLevel !== undefined) {
      setUserLevel(undefined);
      onStateChange(undefined, true);
    }
  }, [shouldShowUserLevel, userLevels, userLevel, deviceModelId, onStateChange]);

  // Determine if user level validation error should be shown
  const userLevelError =
    showErrors && shouldShowUserLevel && userLevel === undefined
      ? t('documents.createFile.validation.userLevelRequired')
      : undefined;

  // Handle device model selection change
  const handleDeviceModelChange = (value: string) => {
    const newDeviceModelId = value === '' ? undefined : Number(value);
    setDeviceModelId(newDeviceModelId);
    setUserLevel(undefined); // Always reset user level when device model changes

    // Notify parent of state change - device model alone is incomplete, so mark as error
    if (newDeviceModelId === undefined) {
      onStateChange(undefined, false); // No selection, no error yet
    } else {
      onStateChange(undefined, true); // Device model selected but user level missing
    }
  };

  // Handle user level selection change
  const handleUserLevelChange = (value: string) => {
    const newUserLevel = value === '' ? undefined : Number(value);
    setUserLevel(newUserLevel);

    // Only notify parent of complete state when both device model and user level are selected
    if (deviceModelId !== undefined && newUserLevel !== undefined) {
      onStateChange(
        { deviceModelId, userLevel: newUserLevel },
        false // Complete and valid state
      );
    } else {
      onStateChange(undefined, true); // Incomplete state, mark as error
    }
  };

  // Render device model dropdown options with error handling
  const renderDeviceModelOptions = () => {
    if (hasDeviceModelError) {
      return <option value="">{t('documents.createFile.failedToLoad')}</option>;
    }

    return (
      <>
        <option value="">{t('documents.createFile.deviceModelNotSet')}</option>
        {deviceModels.map((deviceModel) => (
          <option key={deviceModel.id} value={String(deviceModel.id)}>
            {deviceModel.name}
          </option>
        ))}
      </>
    );
  };

  // Render user level dropdown options with error handling
  const renderUserLevelOptions = () => {
    if (hasUserLevelError) {
      return <option value="">{t('documents.createFile.failedToLoad')}</option>;
    }

    return (
      <>
        <option value="">{t('documents.createFile.selectUserLevel')}</option>
        {userLevels.map((level) => (
          <option key={level.id} value={String(level.id)}>
            {level.name}
          </option>
        ))}
      </>
    );
  };

  // Render the device assignment form section
  return (
    <FormSection
      icon="wrench"
      title={t('documents.createFile.sections.deviceAssignment')}
    >
      <FormSectionInfo>
        {t('documents.createFile.sections.deviceAssignmentDescription')}
      </FormSectionInfo>
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Device model selection dropdown */}
        <FormSelect
          label={t('documents.createFile.fields.deviceModel')}
          value={deviceModelId?.toString() ?? ''}
          onChange={(event) => handleDeviceModelChange(event.target.value)}
          disabled={isLocked}
        >
          {renderDeviceModelOptions()}
        </FormSelect>

        {/* User level selection dropdown - only shown when device model is selected */}
        {shouldShowUserLevel ? (
          <FormSelect
            label={t('documents.createFile.fields.userLevel')}
            value={userLevel?.toString() ?? ''}
            onChange={(event) => handleUserLevelChange(event.target.value)}
            disabled={isLocked}
            error={userLevelError}
          >
            {renderUserLevelOptions()}
          </FormSelect>
        ) : null}
      </div>
    </FormSection>
  );
}
